import type { CertVendor, ProposalBucket } from '../../redux/api/material-certs';

/**
 * The states a heat can be in, in the order a reviewer cares about them.
 * The wording is what shows on the tabs, so it says what is missing rather
 * than naming the backend bucket.
 */
export const BUCKET_ORDER: ProposalBucket[] = [
	'ready',
	'waiting_cert',
	'orphan_cert',
	'supporting_document',
	'no_po',
	'already_certified',
];

/**
 * What the heat list is showing: one bucket, everything still outstanding, or
 * the heats someone rejected. Rejected is not a bucket -- it is a status -- but
 * it belongs on the same control because to a reviewer it is one more shelf to
 * look on.
 */
export type QueueFilter = ProposalBucket | 'all' | 'rejected';

interface BucketMeta {
	label: string;
	/** Tab wording; the full label does not survive a narrow column. */
	short: string;
	color: string;
	/** Shown above the list so a reviewer knows whose move it is. */
	hint: string;
}

export const BUCKETS: Record<ProposalBucket, BucketMeta> = {
	ready: {
		label: 'Ready',
		short: 'Ready',
		color: 'green',
		hint: 'Packing slip, certificate and an ERP line all line up. Match to attach.',
	},
	waiting_cert: {
		label: 'Waiting on cert',
		short: 'No cert',
		color: 'gold',
		hint: 'The vendor shipped the material but has not sent its certificate yet.',
	},
	orphan_cert: {
		label: 'No slip line',
		short: 'No slip',
		color: 'orange',
		hint: 'A certificate arrived that no packing slip or packing list in the system accounts for. Requested Alro certs land here too: pick the ERP line by hand.',
	},
	supporting_document: {
		label: 'Supporting document',
		short: 'Supporting',
		color: 'purple',
		hint: 'McMaster sent no certificate for this line. Match attaches the packing list as a "Packing List" document, which does not mark the line certified.',
	},
	no_po: {
		label: 'No ERP PO',
		short: 'No PO',
		color: 'red',
		hint: 'No material order from this vendor carries this PO number. The number may be mistyped on either side, or the PO may not exist yet — use Find the PO by hand to search for it.',
	},
	already_certified: {
		label: 'Already has a cert',
		short: 'Already has',
		color: 'default',
		hint: 'This exact file is already attached in ERP, so there is nothing to link. Here for auditing only.',
	},
};

interface BucketCounts {
	ready_count: number;
	waiting_cert_count: number;
	orphan_cert_count: number;
	no_po_count: number;
	already_certified_count: number;
	supporting_document_count: number;
}

export const bucketCount = (
	group: BucketCounts,
	bucket: ProposalBucket
): number => {
	switch (bucket) {
		case 'ready':
			return group.ready_count;
		case 'waiting_cert':
			return group.waiting_cert_count;
		case 'orphan_cert':
			return group.orphan_cert_count;
		case 'no_po':
			return group.no_po_count;
		case 'already_certified':
			return group.already_certified_count;
		case 'supporting_document':
			return group.supporting_document_count;
	}
};

export const VENDORS: Record<CertVendor, { label: string; color: string }> = {
	alro: { label: 'Alro', color: 'cyan' },
	mcmaster: { label: 'McMaster', color: 'magenta' },
};

/** What the vendor calls its part number: EDP# at Alro, SKU at McMaster. */
export const partNumberLabel = (vendor: CertVendor | undefined): string =>
	vendor === 'mcmaster' ? 'SKU' : 'EDP#';

/** What the register calls each ending, and how it should read on screen. */
export const DISPOSITIONS = {
	to_review: { label: 'To be reviewed', color: 'blue' },
	linked: { label: 'Linked', color: 'green' },
	manually_linked: { label: 'Manually linked', color: 'cyan' },
	ignored: { label: 'Ignored', color: 'default' },
	failure: { label: 'Failure', color: 'red' },
} as const;

export const DISPOSITION_ORDER = [
	'to_review',
	'linked',
	'manually_linked',
	'ignored',
	'failure',
] as const;

export const MATCH_METHOD_LABEL: Record<string, string> = {
	edp: 'Matched on the vendor part number (EDP# / SKU)',
	type_size_shape: 'Matched on type, size and shape',
	manual: 'Assigned by a reviewer',
	'': 'No ERP line matched',
};
